import { MenuProps } from "antd"
import { faEdit, faSync, faTrash } from "@fortawesome/free-solid-svg-icons"

import useContextMenu from "features/Reporting/hooks/UI/useContextMenu"

import { createMenuItem } from "utils/contextMenuHelpers"

/**
 * Props for useTimeSeriesContextMenu hook
 */
export interface UseTimeSeriesContextMenuProps {
  /** Element ID */
  elementId: string
  /** Whether data is available */
  hasData: boolean
  /** Whether in dashboard mode */
  isDashboard: boolean
  /** Whether currently loading */
  isLoading: boolean
  /** Series keys for dashboard filtering */
  seriesKeys: string[]
  /** Wave identifiers for dashboard filtering */
  waveIdentifiers: string[]
  /** Selected dashboard series */
  selectedDashboardSeries: string[]
  /** Selected dashboard waves */
  selectedDashboardWaves: string[]
  /** Edit handler */
  onEdit: () => void
  /** Refresh handler */
  onRefresh: () => void
  /** Delete handler */
  onDelete: () => void
  /** Series toggle handler */
  onSeriesToggle: (series: string) => void
  /** Wave toggle handler */
  onWaveToggle: (wave: string) => void
}

/**
 * useTimeSeriesContextMenu Hook
 *
 * Provides context menu functionality for time series chart elements.
 * Includes options for editing, refreshing, deleting, and dashboard filtering.
 */
export const useTimeSeriesContextMenu = ({
  hasData,
  isDashboard,
  isLoading,
  seriesKeys,
  waveIdentifiers,
  // May be used for future dashboard filtering features
  selectedDashboardSeries: _selectedDashboardSeries,
  selectedDashboardWaves: _selectedDashboardWaves,
  onEdit,
  onRefresh,
  onDelete,
  onSeriesToggle,
  onWaveToggle,
}: Omit<UseTimeSeriesContextMenuProps, "elementId">) => {
  const menuItems: MenuProps["items"] = []

  // Core menu items
  menuItems.push(
    createMenuItem("edit", faEdit, "Edit Variables", ({ domEvent }) => {
      domEvent.preventDefault()
      onEdit()
    })
  )

  if (hasData) {
    menuItems.push(
      createMenuItem(
        "refresh",
        faSync,
        "Refresh Data",
        ({ domEvent }) => {
          domEvent.preventDefault()
          onRefresh()
        },
        {
          disabled: isLoading,
        }
      )
    )
  }

  // Dashboard-specific menu items for series filtering
  if (hasData && isDashboard && seriesKeys.length > 1) {
    const seriesSubmenuItems: MenuProps["items"] = seriesKeys.map((series) => ({
      key: `series-${series}`,
      label: series,
      onClick: ({ domEvent }) => {
        domEvent.preventDefault()
        onSeriesToggle(series)
      },
    }))

    menuItems.push({
      key: "series-filter",
      label: "Filter Series",
      children: seriesSubmenuItems,
    })
  }

  // Dashboard-specific menu items for wave filtering
  if (hasData && isDashboard && waveIdentifiers.length > 1) {
    const waveSubmenuItems: MenuProps["items"] = waveIdentifiers.map(
      (wave) => ({
        key: `wave-${wave}`,
        label: wave,
        onClick: ({ domEvent }) => {
          domEvent.preventDefault()
          onWaveToggle(wave)
        },
      })
    )

    menuItems.push({
      key: "wave-filter",
      label: "Filter Waves",
      children: waveSubmenuItems,
    })
  }

  // Divider and delete
  if (menuItems.length > 0) {
    menuItems.push({
      type: "divider",
      key: "divider",
    })
  }

  menuItems.push(
    createMenuItem(
      "delete",
      faTrash,
      "Delete",
      ({ domEvent }) => {
        domEvent.preventDefault()
        onDelete()
      },
      {
        danger: true,
      }
    )
  )

  return useContextMenu({
    menuItems,
  })
}
